/* WI-12031 PR 8 — modal for finance to paste Clementine delivery rows via CSV.
 *
 * The Clementine Sync Platform (WI-11719) will populate this table on a
 * schedule eventually. Until that lands, finance pastes CSV into this modal
 * and the reconciliation page picks up DELIVERED_NOT_BILLED rows.
 */

import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { X, Upload, AlertTriangle } from 'lucide-react'

import { api, ApiError } from '@/lib/api/client'


const EXAMPLE = [
  'customerId,analyzedCompanyId,productArticleId,volumeDescription,deliveredLastAt',
  '42,100,200,"312 syncs/mo",2026-05-15',
  '42,101,200,"18.4M API calls/mo",2026-05-15',
].join('\n')


export function ImportDeliveriesModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [csv,   setCsv]   = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation<{ imported: number; inserted: number; updated: number }, ApiError>({
    mutationFn: async () => api.post('/clementine/deliveries/import', { csv }),
    onSuccess: (res) => {
      toast.success(
        `Imported ${res.imported} row${res.imported === 1 ? '' : 's'} `
        + `(${res.inserted} new, ${res.updated} updated)`,
      )
      qc.invalidateQueries({ queryKey: ['reconciliation']         })
      qc.invalidateQueries({ queryKey: ['clementine-deliveries']  })
      onClose()
    },
    onError: (e) => {
      // 400 → bad CSV; surface inline so finance sees which row to fix.
      // 500-ish → toast.
      if (e instanceof ApiError && e.status === 400) {
        setError(e.message)
      } else {
        toast.error(e instanceof ApiError ? e.message : 'Import failed')
      }
    },
  })

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'color-mix(in oklch, var(--ns-text) 30%, transparent)',
        backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '8vh 16px',
        overflow: 'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="card"
        style={{ maxWidth: 680, width: '100%', padding: 22, boxShadow: 'var(--ns-shadow)' }}
      >
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700 }}>Import Clementine deliveries</div>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 4, maxWidth: 540 }}>
              Paste CSV with one row per (customer × analyzed company × article). The reconciliation
              view picks up these rows as the "delivered" side. Existing rows for the same
              combination are <strong>updated</strong> rather than duplicated.
            </div>
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </div>

        <div style={{ marginTop: 16 }}>
          <div className="muted" style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>
            Required columns: customerId, analyzedCompanyId, productArticleId
            <br />
            Optional: deliveredFirstAt, deliveredLastAt (YYYY-MM-DD), volumeDescription
          </div>
          <textarea
            className="input mono"
            style={{ width: '100%', minHeight: 160, fontSize: 12.5, fontFamily: 'JetBrains Mono, ui-monospace, monospace' }}
            placeholder={EXAMPLE}
            value={csv}
            onChange={e => { setCsv(e.target.value); setError(null) }}
          />
        </div>

        {error && (
          <div className="card" style={{ marginTop: 12, padding: 12, borderLeft: '3px solid var(--ns-err)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <AlertTriangle size={14} style={{ color: 'var(--ns-err)' }} />
              <strong style={{ fontSize: 13 }}>{error}</strong>
            </div>
          </div>
        )}

        <div className="card" style={{ marginTop: 12, padding: 10, background: 'var(--ns-surface-2)' }}>
          <div className="muted" style={{ fontSize: 11.5 }}>
            <strong>Heads-up:</strong> manual imports are tagged <code>MANUAL_IMPORT</code>.
            When the Clementine Sync Platform (WI-11719) ships, it'll re-populate these rows from
            the source of truth — manual rows will be overwritten as they're refreshed.
          </div>
        </div>

        <div className="row" style={{ gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-brand"
            disabled={!csv.trim() || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? 'Importing…' : <><Upload size={13} /> Import</>}
          </button>
        </div>
      </div>
    </div>
  )
}
